import React from 'react';
import { Target, Eye, Heart, TrendingUp, Shield, Lightbulb } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const About = () => {
  const { t } = useLanguage();

  const values = [
    {
      icon: Shield,
      title: t.about.values.integrity,
      description: t.about.values.integrityDesc
    },
    {
      icon: TrendingUp,
      title: t.about.values.excellence,
      description: t.about.values.excellenceDesc
    },
    {
      icon: Lightbulb,
      title: t.about.values.innovation,
      description: t.about.values.innovationDesc
    },
    {
      icon: Heart,
      title: t.about.values.partnership,
      description: t.about.values.partnershipDesc
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative h-96 flex items-center">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.pexels.com/photos/5864750/pexels-photo-5864750.jpeg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="absolute inset-0 bg-black opacity-60"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
          <h1 className="text-5xl font-bold mb-4">{t.about.hero.title}</h1>
          <p className="text-xl text-gray-200">{t.about.hero.subtitle}</p>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Mission */}
            <div className="bg-gray-50 p-8 rounded-2xl">
              <div className="w-16 h-16 bg-red-100 rounded-xl flex items-center justify-center mb-6">
                <Target className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                {t.about.mission.title}
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                {t.about.mission.description}
              </p>
            </div>

            {/* Vision */}
            <div className="bg-gray-50 p-8 rounded-2xl">
              <div className="w-16 h-16 bg-red-100 rounded-xl flex items-center justify-center mb-6">
                <Eye className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                {t.about.vision.title}
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                {t.about.vision.description}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t.about.values.title}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-14 h-14 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                  <value.icon className="w-7 h-7 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {value.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Image Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="relative h-80 rounded-2xl overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1610444833641-0542660a4ed7"
                alt="Fresh Produce"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="relative h-80 rounded-2xl overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1610832958506-aa56368176cf"
                alt="Quality Products"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
